import { useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';

import dotize from 'dotize';
import cloneDeep from 'lodash-es/cloneDeep';
import merge from 'lodash-es/merge';
import set from 'lodash-es/set';
import {
  EllipsisVerticalIcon,
  LockIcon,
  LockOpenIcon,
  Maximize2Icon,
  Trash2Icon,
} from 'lucide-react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import TextareaAutosize from 'react-textarea-autosize';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { Button } from '@/components/ui/button';
import { AutocompleteOption, Autocomplete } from '@/components/ui/custom/Autocomplete/Autocomplete';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { updateContext } from '@/modules/tryIt/redux/actions';
import { selectContext, selectIsSending } from '@/modules/tryIt/redux/selectors';
import { removeEmptyObjects } from '@/util/util';

import { userContextVariables } from './constants';
import { customInputValueRender } from './helper';
import { useVariablesSection } from './useVariablesSection';
import { StateContextVariable } from '../../types';

import styles from './VariablesSection.module.scss';

interface ContextVariableFormValues {
  variableKey: string;
  variableValue: string;
}

type ContextVariableProps = {
  variable: StateContextVariable;
  canEdit: boolean;
  onButtonClick?: () => void;
  isLast?: boolean;
};

export const ContextVariable = ({
  variable,
  canEdit,
  onButtonClick = undefined,
  isLast = false,
}: ContextVariableProps) => {
  // Custom hooks
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { getVariableOption } = useVariablesSection();

  // Redux
  const storeContext = useSelector(selectContext);
  const isLoadingResponse = useSelector(selectIsSending);

  const variableOption = useMemo(() => getVariableOption(variable), [getVariableOption, variable]);

  const { getValues, setValue, control } = useForm<ContextVariableFormValues>({
    mode: 'onChange',
    defaultValues: {
      variableKey: variableOption?.value ?? '',
      variableValue: customInputValueRender(variable.value),
    },
  });

  const variableKeyValue = useWatch({ control, name: 'variableKey' });
  const variableValueValue = useWatch({ control, name: 'variableValue' });

  // Local state
  const [isCollapsed, setIsCollapsed] = useState(true);

  // Truncation detection for tooltips
  const keySpanRef = useRef<HTMLSpanElement>(null);
  const valueSpanRef = useRef<HTMLSpanElement>(null);
  const [isKeyTruncated, setIsKeyTruncated] = useState(false);
  const [isValueTruncated, setIsValueTruncated] = useState(false);

  useEffect(() => {
    const el =
      keySpanRef.current?.querySelector<HTMLElement>('input') ??
      keySpanRef.current?.querySelector<HTMLElement>('[data-truncation-target]');
    if (!el) {
      return;
    }
    const update = () => setIsKeyTruncated(el.scrollWidth > el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variableKeyValue]);

  useEffect(() => {
    if (!isCollapsed) {
      setIsValueTruncated(false);
      return;
    }
    const el = valueSpanRef.current?.querySelector<HTMLElement>('input');
    if (!el) {
      return;
    }
    const update = () => setIsValueTruncated(el.scrollWidth > el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [variableValueValue, isCollapsed]);

  // Local variables
  const iconProps = { size: 18 };
  const showLockIcon = variable.isLocked;
  const canDeleteVariable = !variable.isLocked && !isLast && canEdit;
  const isInputDisabled = variable.isLocked && canEdit;
  const isKeyInputFilled = !!variableKeyValue;

  const autocompleteOptions: AutocompleteOption[] = useMemo(
    () =>
      userContextVariables?.map((variable) => ({
        value: variable.label,
        label: t(`auto_complete.variable_labels.${variable.label}`),
        category: t('analytics.fields.user'),
      })),
    [t]
  );

  if (variable.value === null && !isLast) {
    return null;
  }

  const deleteContextVariable = () => {
    const oldContext = cloneDeep(storeContext).slice(0, -1) ?? [];
    const newContext = cloneDeep(storeContext[storeContext.length - 1]) ?? {};

    set(newContext, variable.label, null);

    dispatch(updateContext([...oldContext, removeEmptyObjects(newContext)]));
  };

  const clearInputFields = () => {
    setValue('variableKey', '');
    setValue('variableValue', '');
  };

  const handleBlur = () => {
    if (!canEdit) {
      return;
    }

    const value = getValues('variableKey');
    const variableValue = getValues('variableValue');

    // Prevents updating when one of the inputs is empty or value is not changed
    if (!value || !variableValue || variable.value === variableValue) {
      return;
    }

    const oldContext = cloneDeep(storeContext).slice(0, -1) ?? [];
    const newContext = cloneDeep(storeContext[storeContext.length - 1]) ?? {};

    merge(newContext, dotize.backward({ [value]: variableValue }));
    if (isLast) {
      clearInputFields();
    }

    dispatch(updateContext([...oldContext, newContext]));
  };

  return (
    <div className={styles.wrapper}>
      {canEdit && (
        <Button
          variant="ghost-neutral"
          size="icon-sm"
          aria-label={t('try_it.modal.toggle_lock')}
          onClick={onButtonClick}
          disabled={isLoadingResponse}
          className={cn({ hidden: isLast })}
        >
          {showLockIcon ? (
            <LockIcon {...iconProps} size={16} />
          ) : (
            <LockOpenIcon {...iconProps} size={16} />
          )}
        </Button>
      )}
      <div className={cn({ [styles.inputsWrapper]: true, [styles.fullWidth]: !isCollapsed })}>
        <InfoTooltip
          content={isKeyTruncated ? variableKeyValue : undefined}
          side="top"
          size="large"
        >
          <span
            ref={keySpanRef}
            className={cn({
              [styles.key]: true,
              [styles['key--cannotEdit']]: !canEdit,
              [styles['key--fullWidth']]: !isCollapsed,
              [styles.isFilled]: isKeyInputFilled && canEdit,
            })}
            data-testid="key-input-wrapper"
          >
            {canEdit ? (
              <Autocomplete
                options={autocompleteOptions}
                value={variableKeyValue}
                onChange={(newValue) => setValue('variableKey', newValue)}
                disabled={isInputDisabled || isKeyInputFilled}
                placeholder={t('dialog.set_variables.variable_name.placeholder')}
                searchPlaceholder={t('common.search_dots')}
                onCreateNew={(newValue) => setValue('variableKey', newValue)}
                className="[&_button]:h-[30px] [&_button]:border-0 [&_button]:py-1 [&_button]:text-xs"
              />
            ) : (
              <Controller
                control={control}
                name="variableKey"
                render={({ field }) => (
                  <Input
                    readOnly={!canEdit}
                    size="sm"
                    className="bg-muted/50 text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            )}
          </span>
        </InfoTooltip>
        <InfoTooltip
          content={isCollapsed && isValueTruncated ? variableValueValue : undefined}
          side="top"
          size="large"
        >
          <span
            ref={valueSpanRef}
            className={cn({ [styles.value]: true, [styles['value--fullWidth']]: !isCollapsed })}
            data-testid="value-input-wrapper"
          >
            <Controller
              control={control}
              name="variableValue"
              render={({ field, fieldState: { error } }) =>
                isCollapsed ? (
                  <Input
                    disabled={isInputDisabled}
                    readOnly={!canEdit}
                    size="sm"
                    placeholder={t('common.type_value')}
                    aria-invalid={!!error?.message}
                    className={cn(
                      isInputDisabled && 'disabled:opacity-100',
                      !canEdit &&
                        'bg-muted/50 text-muted-foreground focus-visible:ring-0 focus-visible:ring-offset-0'
                    )}
                    {...field}
                    value={field.value ?? ''}
                    onBlur={() => {
                      field.onBlur();
                      handleBlur();
                    }}
                    onKeyUp={(e) => {
                      if (e.key === 'Enter') {
                        handleBlur();
                      }
                    }}
                  />
                ) : (
                  <TextareaAutosize
                    disabled={isInputDisabled}
                    readOnly={!canEdit}
                    className={styles.textarea}
                    placeholder={t('common.type_value')}
                    minRows={4}
                    maxRows={6}
                    {...field}
                    value={field.value ?? ''}
                  />
                )
              }
            />
          </span>
        </InfoTooltip>
      </div>

      <DropdownMenu modal={false}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost-neutral"
            size="icon-sm"
            aria-label={t('common.settings')}
            className={cn({ [styles.hidden]: isLast })}
          >
            <EllipsisVerticalIcon size={18} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[1400]">
          <DropdownMenuItem
            onClick={() => {
              setIsCollapsed((prev) => !prev);
            }}
          >
            <Maximize2Icon size={16} />
            {isCollapsed ? t('common.expand') : t('common.collapse')}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant="destructive"
            onClick={deleteContextVariable}
            disabled={!canDeleteVariable}
          >
            <Trash2Icon size={16} />
            {t('common.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
